import { ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Link } from 'react-router-dom'

interface TreatmentCardProps {
  to?: string
  title: string
  description?: string
  imageSrc?: string
  imageAlt?: string
  disabled?: boolean
  className?: string
}

export function TreatmentCard({
  to,
  title,
  description,
  imageSrc,
  imageAlt,
  disabled = false,
  className,
}: TreatmentCardProps) {
  const { t } = useTranslation()
  const isClickable = !disabled && Boolean(to)

  const containerClasses = cn(
    'group flex flex-col h-full bg-cream rounded-xl overflow-hidden',
    'border border-white/50 border-t-2 border-t-transparent shadow-sm',
    isClickable && [
      'hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/10 hover:border-primary/30 hover:border-t-primary',
      'transition-all duration-300',
    ],
    !isClickable && 'opacity-80',
    className
  )

  const content = (
    <>
      {imageSrc ? (
        <div className="aspect-[16/10] overflow-hidden">
          <img
            src={imageSrc}
            alt={imageAlt || title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      ) : (
        <div className="aspect-[16/10] bg-primary-soft flex items-center justify-center">
          <span className="font-display text-3xl text-primary/40">{title.charAt(0)}</span>
        </div>
      )}
      <div className="flex flex-col flex-grow justify-between p-6">
        <div>
          <h3
            className={cn(
              'font-display text-xl font-semibold text-ink transition-colors duration-300',
              isClickable && 'group-hover:text-primary'
            )}
          >
            {title}
          </h3>
          {description && (
            <p className="mt-2 text-sm text-muted leading-relaxed">{description}</p>
          )}
        </div>
        <span
          className={cn(
            'mt-4 inline-flex items-center gap-1 text-sm font-medium text-ink transition-colors',
            isClickable && 'group-hover:text-primary'
          )}
        >
          {isClickable ? t('buttons.learnMore') : t('common:buttons.comingSoon', 'In arrivo')}
          <ArrowRight
            className={cn(
              'w-4 h-4 transition-transform duration-300',
              isClickable && 'group-hover:translate-x-1'
            )}
          />
        </span>
      </div>
    </>
  )

  if (!isClickable) {
    return <div className={containerClasses}>{content}</div>
  }

  return (
    <Link to={to!} className={containerClasses}>
      {content}
    </Link>
  )
}
